import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, RefreshCw, ExternalLink, Globe, Trash2, ChevronRight, Server } from 'lucide-react';
import api from '../lib/api';
import { asArray } from '../lib/safeData';
import { StatusBadge } from '../components/StatusBadge';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const CONNECTOR_TYPES = ['generic', 'pro_email', 'email_extractor'];
const ENVIRONMENTS = ['prod', 'stage', 'local'];
const AUTH_TYPES = ['none', 'api_key', 'jwt', 'basic'];

function AddAppModal({ connectorTypes, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', app_type: 'web', connector_type: 'generic', base_url: '', health_path: '', environment: 'prod', auth_type: 'none', notes: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/apps/', form);
      toast.success(`App "${form.name}" added`);
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add app');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="add-app-modal">
      <div className="glass rounded-xl border border-white/10 w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h3 className="font-heading font-bold text-foreground">Register New App</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors text-lg leading-none">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">App Name *</label>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Pro-Email" className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none transition-colors" data-testid="app-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Connector Type *</label>
              <select value={form.connector_type} onChange={e => setForm({ ...form, connector_type: e.target.value })} className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground outline-none transition-colors" data-testid="connector-type-select">
                {connectorTypes.map(ct => <option key={ct.type} value={ct.type}>{ct.display_name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Base URL *</label>
            <input required value={form.base_url} onChange={e => setForm({ ...form, base_url: e.target.value })} placeholder="https://pro-email.app" className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none transition-colors font-mono" data-testid="app-base-url-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Health Path <span className="font-normal">(optional)</span></label>
            <input value={form.health_path} onChange={e => setForm({ ...form, health_path: e.target.value })} placeholder="/api/health" className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none transition-colors font-mono" data-testid="app-health-path-input" />
            <p className="text-xs text-muted-foreground mt-1">Uses the base URL when left empty.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Environment</label>
              <select value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value })} className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground outline-none transition-colors" data-testid="app-env-select">
                {ENVIRONMENTS.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Auth Type</label>
              <select value={form.auth_type} onChange={e => setForm({ ...form, auth_type: e.target.value })} className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground outline-none transition-colors" data-testid="app-auth-type-select">
                {AUTH_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Optional notes..." className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none transition-colors resize-none" data-testid="app-notes-input" />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-white/10 text-muted-foreground hover:text-foreground transition-colors text-sm">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium transition-colors text-sm disabled:opacity-50" data-testid="add-app-submit-btn">
              {loading ? 'Adding...' : 'Register App'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Apps() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [apps, setApps] = useState([]);
  const [connectorTypes, setConnectorTypes] = useState([]);
  const [showModal, setShowModal] = useState(searchParams.get('add') === 'true');
  const [checking, setChecking] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [appsRes, ctRes] = await Promise.all([api.get('/apps/'), api.get('/apps/connector-types')]);
      setApps(asArray(appsRes.data));
      setConnectorTypes(asArray(ctRes.data));
    } catch { toast.error('Failed to load apps'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/apps/${id}`);
      toast.success('App removed');
      setApps(apps.filter(a => a.id !== id));
    } catch { toast.error('Delete failed'); }
  };

  const runCheck = async (app) => {
    setChecking(app.id);
    try {
      await api.post(`/apps/${app.id}/health-check`);
      toast.success(`Health check complete`);
      await fetchData();
    } catch { toast.error('Health check failed'); }
    finally { setChecking(null); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="apps-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">App Registry</h1>
          <p className="text-muted-foreground text-sm mt-0.5">{apps.length} connected app{apps.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-medium transition-colors active:scale-95"
          data-testid="add-app-btn"
        >
          <Plus className="w-4 h-4" />
          Register App
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : apps.length === 0 ? (
        <div className="glass rounded-xl border border-white/5 p-16 text-center">
          <Server className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-bold text-foreground mb-2">No Apps Registered</h3>
          <p className="text-muted-foreground text-sm mb-6">Connect your first app to start monitoring its health.</p>
          <button onClick={() => setShowModal(true)} className="px-6 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-medium transition-colors" data-testid="empty-add-app-btn">
            Register First App
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {apps.map(app => (
            <div key={app.id} className="glass rounded-xl border border-white/5 hover:border-primary/20 transition-colors group" data-testid={`app-card-${app.id}`}>
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-primary" />
                      <span className="font-heading font-bold text-foreground">{app.name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono mt-0.5 truncate max-w-[200px]">{app.base_url}</div>
                  </div>
                  <StatusBadge status={app.latest_health?.status || 'unknown'} />
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-secondary/50 text-muted-foreground font-mono">{app.environment}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-secondary/50 text-muted-foreground font-mono">{app.connector_type}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-secondary/50 text-muted-foreground font-mono">{app.auth_type}</span>
                </div>

                {app.latest_health && (
                  <div className="text-xs text-muted-foreground font-mono mb-4">
                    Last check: {formatDistanceToNow(new Date(app.latest_health.checked_at), { addSuffix: true })}
                    {app.latest_health.response_time_ms != null && ` · ${app.latest_health.response_time_ms}ms`}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => runCheck(app)}
                    disabled={checking === app.id}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-white/10 hover:border-primary/30 text-muted-foreground hover:text-foreground text-xs font-medium transition-colors"
                    data-testid={`run-check-btn-${app.id}`}
                  >
                    <RefreshCw className={`w-3 h-3 ${checking === app.id ? 'animate-spin' : ''}`} />
                    Check
                  </button>
                  <button
                    onClick={() => navigate(`/apps/${app.id}`)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-primary/20 hover:border-primary/50 bg-primary/10 hover:bg-primary/15 text-primary text-xs font-medium transition-colors"
                    data-testid={`manage-app-btn-${app.id}`}
                  >
                    Manage
                    <ChevronRight className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => handleDelete(app.id, app.name)}
                    className="p-2 rounded-lg border border-white/10 hover:border-destructive/30 text-muted-foreground hover:text-destructive transition-colors"
                    data-testid={`delete-app-btn-${app.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <AddAppModal
          connectorTypes={connectorTypes}
          onClose={() => setShowModal(false)}
          onCreated={(app) => setApps([app, ...apps])}
        />
      )}
    </div>
  );
}
